import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, UserPlus, CheckCircle, Briefcase, Search, ArrowRight, Star, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const HowItWorksPage = () => {
  const navigate = useNavigate();

  const professionalSteps = [
    {
      number: 1,
      icon: UserPlus,
      title: 'Create Your Profile',
      description: 'Sign up and build a comprehensive profile showcasing your experience, skills, portfolio, and past projects. Add your location and department specialization.'
    },
    {
      number: 2,
      icon: CheckCircle,
      title: 'Get Verified',
      description: 'Our team reviews your profile and credentials. Verified professionals get a badge, making you more discoverable and trustworthy to potential clients.'
    },
    {
      number: 3,
      icon: Search,
      title: 'Get Discovered',
      description: 'Production houses and clients search for professionals by location and department. Your profile appears in relevant searches, bringing opportunities to you.'
    },
    {
      number: 4,
      icon: Briefcase,
      title: 'Apply & Connect',
      description: 'Browse active hiring posts, apply to projects that match your skills, and connect directly with clients to discuss opportunities and rates.'
    }
  ];

  const clientSteps = [
    {
      number: 1,
      icon: Briefcase,
      title: 'Create a Hiring Post',
      description: 'Post your project details including departments needed, roles, locations, timeline, and budget. Select multiple departments if hiring a full crew.'
    },
    {
      number: 2,
      icon: Search,
      title: 'Browse & Filter',
      description: 'Search our database of verified professionals by department, location, experience level, and specialization. View portfolios and past work.'
    },
    {
      number: 3,
      icon: Star,
      title: 'Review & Shortlist',
      description: 'Check ratings, reviews, and detailed profiles. Shortlist candidates who fit your project requirements and budget.'
    },
    {
      number: 4,
      icon: MessageCircle,
      title: 'Connect & Hire',
      description: 'Reach out directly to shortlisted professionals, discuss project details, negotiate terms, and finalize your crew for the shoot.'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="mb-4 bg-accent/10 text-accent border-accent/30">
              How It Works
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
              Simple Steps to
              <span className="text-accent"> Connect & Create</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Whether you're a professional or a client, getting started on Shoot Base is easy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* For Professionals */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-3 bg-accent/10 text-accent border-accent/30">
              For Film Professionals
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-foreground">Find Your Next Project</h2>
            <p className="text-muted-foreground text-base max-w-2xl mx-auto">
              Build your profile, get discovered, and connect with production houses across India
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {professionalSteps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full bg-card border-border hover:border-accent/50 transition-all hover:shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                          {step.number}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3">
                            <step.icon className="w-5 h-5 text-accent" />
                            <h3 className="text-xl font-semibold text-foreground">{step.title}</h3>
                          </div>
                          <p className="text-muted-foreground text-sm leading-relaxed">{step.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-10"
            >
              <Button 
                size="lg" 
                className="bg-accent hover:bg-accent/90 text-primary-foreground font-semibold group"
                onClick={() => navigate('/create-profile')}
              >
                Join as Professional
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* For Clients */}
      <section className="py-16 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-3 bg-accent/10 text-accent border-accent/30">
              For Production Houses & Clients
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-foreground">Hire the Perfect Crew</h2>
            <p className="text-muted-foreground text-base max-w-2xl mx-auto">
              Post your project, browse verified professionals, and build your dream team
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {clientSteps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full bg-card border-border hover:border-accent/50 transition-all hover:shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                          {step.number}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3">
                            <step.icon className="w-5 h-5 text-accent" />
                            <h3 className="text-xl font-semibold text-foreground">{step.title}</h3>
                          </div>
                          <p className="text-muted-foreground text-sm leading-relaxed">{step.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-10"
            >
              <Button 
                size="lg" 
                className="bg-accent hover:bg-accent/90 text-primary-foreground font-semibold group"
                onClick={() => navigate('/post-project')}
              >
                Create a Hiring Post
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl font-bold mb-4 text-foreground">Still Have Questions?</h2>
            <p className="text-muted-foreground mb-6">
              Visit our Help Center for detailed FAQs or reach out to our support team.
            </p>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate('/help')}>
                Visit Help Center
              </Button>
              <Button variant="outline" onClick={() => navigate('/contact')}>
                Contact Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;